import { ref, computed, watch, reactive } from 'vue'
import { useLogger } from '@/composables/useLogger'

const logger = useLogger('useCurrency');

const STORAGE_KEY = 'usenlease-currency'
const RATES_STORAGE_KEY = 'usenlease-exchange-rates'
const RATES_TIMESTAMP_KEY = 'usenlease-exchange-rates-ts'
const RATES_TTL = 1000 * 60 * 60 // refresh rates every hour

// Base currency stored in the database is USD
const BASE_CURRENCY = 'USD'

const currencies = [
  { code: 'USD', symbol: '$', name: 'US Dollar', locale: 'en-US' },
  { code: 'KES', symbol: 'Ksh', name: 'Kenyan Shilling', locale: 'en-KE' },
  { code: 'GBP', symbol: '£', name: 'British Pound', locale: 'en-GB' },
  { code: 'EUR', symbol: '€', name: 'Euro', locale: 'de-DE' },
]

// Fallback rates (USD → X) — updated periodically as a safety net
const FALLBACK_RATES = { USD: 1, KES: 129.50, GBP: 0.79, EUR: 0.92 }

// Shared reactive state (singleton across all components)
const currentCode = ref(localStorage.getItem(STORAGE_KEY) || 'USD')
const rates = reactive(loadCachedRates())
const ratesReady = ref(!!localStorage.getItem(RATES_STORAGE_KEY))

watch(currentCode, (v) => localStorage.setItem(STORAGE_KEY, v))

const currency = computed(() =>
  currencies.find((c) => c.code === currentCode.value) || currencies[0]
)

/* ── Rate helpers ─────────────────────────────────────── */

function loadCachedRates() {
  try {
    const raw = localStorage.getItem(RATES_STORAGE_KEY)
    const ts = Number(localStorage.getItem(RATES_TIMESTAMP_KEY) || 0)
    if (raw && Date.now() - ts < RATES_TTL) return JSON.parse(raw)
  } catch { /* ignore corrupt cache */ }
  return { ...FALLBACK_RATES }
}

function saveRates(rateObj) {
  Object.assign(rates, rateObj)
  ratesReady.value = true
  localStorage.setItem(RATES_STORAGE_KEY, JSON.stringify(rateObj))
  localStorage.setItem(RATES_TIMESTAMP_KEY, String(Date.now()))
}

let fetchPromise = null

async function fetchRates() {
  if (fetchPromise) return fetchPromise

  fetchPromise = (async () => {
    const targets = currencies.map((c) => c.code).filter((c) => c !== BASE_CURRENCY)

    // ── Source 1: Frankfurter (European Central Bank data, very reliable) ──
    try {
      const res = await fetch(
        `https://api.frankfurter.app/latest?from=${BASE_CURRENCY}&to=${targets.join(',')}`
      )
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      // data.rates → { EUR: 0.92, GBP: 0.79, KES: 129.5 }
      const wanted = { [BASE_CURRENCY]: 1 }
      for (const c of targets) {
        wanted[c] = data.rates?.[c] ?? FALLBACK_RATES[c] ?? 1
      }
      saveRates(wanted)
      return
    } catch (e) {
      logger.warn('Frankfurter rate source failed, trying fallback', { message: e.message })
    }

    // ── Source 2: Open Exchange Rate API (no key required for v6) ──
    try {
      const res = await fetch(
        `https://open.er-api.com/v6/latest/${BASE_CURRENCY}`
      )
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const wanted = { [BASE_CURRENCY]: 1 }
      for (const c of targets) {
        wanted[c] = data.rates?.[c] ?? FALLBACK_RATES[c] ?? 1
      }
      saveRates(wanted)
      return
    } catch (e) {
      logger.warn('open.er-api rate source failed, using cached/fallback', { message: e.message })
    }

    // If both fail, keep whatever is already in `rates` (cached or hardcoded fallback)
  })()

  fetchPromise = fetchPromise.finally(() => { fetchPromise = null })
  return fetchPromise
}

// Kick off a background fetch on first import (non-blocking)
const cachedTs = Number(localStorage.getItem(RATES_TIMESTAMP_KEY) || 0)
if (Date.now() - cachedTs >= RATES_TTL) {
  fetchRates()
}

/* ── Public API ───────────────────────────────────────── */

function setCurrency(code) {
  currentCode.value = code
}

function convert(amountInUsd) {
  const rate = rates[currentCode.value] ?? 1
  return amountInUsd * rate
}

function formatPrice(amount) {
  const n = Number(amount)
  const cur = currency.value
  if (isNaN(n)) return `${cur.symbol}0.00`
  const converted = convert(n)
  const sym = cur.symbol
  // Add a space after text-based symbols (e.g. "Ksh 1,232.33") but not after glyph symbols ($, £, €)
  const separator = sym.length > 1 ? ' ' : ''
  // Use locale-aware formatting with comma thousands separators
  const formatted = converted.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return `${sym}${separator}${formatted}`
}

// Number-only formatting (no currency symbol) — useful when an icon already shows the currency
function formatAmount(amount) {
  const n = Number(amount)
  if (isNaN(n)) return '0.00'
  const converted = convert(n)
  return converted.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export function useCurrency() {
  return { currencies, currency, currentCode, setCurrency, formatPrice, formatAmount, convert, fetchRates, ratesReady }
}
